import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { Button } from "../../../components/forms/Buttons/Button";
import Input from "../../../components/forms/Input/Input";
import Select from "../../../components/forms/Select/Select";
import { patchTeam } from "../../../services/olympiad/teamService";
import { getRegions } from "../../../services/olympiad/regionService";

export default function EditTeamForm({ team, onCancel, onSuccess }) {
  const [t] = useTranslation();
  const [loading, setLoading] = useState(false);
  const [regions, setRegions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      teamName: team?.name || "",
      region: team?.region?.uuid || "",
      district: team?.district || "",
      city: team?.city || "",
      school: team?.school || "",
      examCenter: team?.exam_center?.uuid || "",
    },
  });

  const watchedRegion = watch("region");

  useEffect(() => {
    const fetchOptions = () => {
      setLoadingOptions(true);
      Promise.all([getRegions({ is_full: false })])
        .then(([availableRegionsData]) => {
          if (
            !availableRegionsData.results.find(
              (region) => region.uuid === team.region.uuid
            )
          ) {
            setRegions([team.region, ...availableRegionsData.results]);
          } else {
            setRegions(availableRegionsData.results);
          }

          // Set initial selected region
          const initialRegion =
            availableRegionsData.results.find(
              (r) => r.uuid === team?.region?.uuid
            ) || team?.region;
          setSelectedRegion(initialRegion);
        })
        .catch((error) => {
          console.error("Error fetching options:", error);
        })
        .finally(() => {
          setLoadingOptions(false);
        });
    };

    fetchOptions();
  }, []);

  useEffect(() => {
    if (watchedRegion) {
      const region = regions.find((r) => r.uuid === watchedRegion);
      setSelectedRegion(region || null);
    } else {
      setSelectedRegion(null);
    }
  }, [watchedRegion, regions]);

  const onSubmit = (data) => {
    setLoading(true);
    setError(null);
    const teamData = {
      name: data.teamName,
      region_uuid: data.region,
      school: data.school,
    };

    // Only include district and city if region is not republican
    if (selectedRegion && !selectedRegion.is_republican_city) {
      if (data.district) {
        teamData.district = data.district;
      }
      if (data.city) {
        teamData.city = data.city;
      }
    }

    patchTeam(team.uuid, teamData)
      .then((response) => {
        onSuccess(response);
      })
      .catch((error) => {
        console.error("Error updating team:", error);
        setError(error.response?.data?.detail || t("team.error_updating_team"));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const regionOptions = regions.map((region) => ({
    label: region.name || region.uuid,
    value: region.uuid,
  }));

  return (
    <div className="border-t border-gray-300 pt-6 mt-6">
      <h3 className="text-lg font-medium mb-4">{t("team.edit_team_info")}</h3>
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <Input
          label={t("team.team_name")}
          placeholder={t("team.enter_team_name")}
          errorMsg={errors?.teamName?.message}
          inputClassName={`bg-white shadow-none outline-1 outline-gray-300 md:rounded-lg h-10 ${
            errors?.teamName?.message && "ring-error ring-1"
          }`}
          className="w-full text-sm"
          type="text"
          required
          {...register("teamName", {
            required: t("team.team_name_required"),
            minLength: {
              value: 3,
              message: t("team.team_name_min_length"),
            },
            maxLength: {
              value: 50,
              message: t("team.team_name_max_length"),
            },
          })}
        />
        <p className="text-xs text-gray-500 mt-1">
          {" "}
          {t("team.team_name_hint")}{" "}
        </p>

        <div>
          <Controller
            name="region"
            control={control}
            rules={{ required: t("team.region_required") }}
            render={({ field }) => (
              <Select
                label={t("team.region")}
                placeholder={t("team.select_region")}
                options={regionOptions}
                value={field.value}
                onChange={(option) => field.onChange(option?.value)}
                errorMsg={errors?.region?.message}
                loading={loadingOptions}
                required
                className="w-full text-sm"
              />
            )}
          />
          <p className="text-xs text-gray-500 mt-1">
            {t("team.region_not_in_list_hint")}
          </p>
        </div>

        {selectedRegion && !selectedRegion.is_republican_city && (
          <Input
            label={t("team.district")}
            placeholder={t("team.enter_district")}
            errorMsg={errors?.district?.message}
            inputClassName={`bg-white shadow-none outline-1 outline-gray-300 md:rounded-lg h-10 ${
              errors?.district?.message && "ring-error ring-1"
            }`}
            className="w-full text-sm"
            type="text"
            required
            {...register("district", {
              required: t("team.district_required"),
            })}
          />
        )}

        {selectedRegion && !selectedRegion.is_republican_city && (
          <Input
            label={t("team.city")}
            placeholder={t("team.enter_city")}
            errorMsg={errors?.city?.message}
            inputClassName={`bg-white shadow-none outline-1 outline-gray-300 md:rounded-lg h-10 ${
              errors?.city?.message && "ring-error ring-1"
            }`}
            className="w-full text-sm"
            type="text"
            required
            {...register("city", {
              required: t("team.city_required"),
            })}
          />
        )}

        <Input
          label={t("team.school")}
          placeholder={t("team.enter_school")}
          errorMsg={errors?.school?.message}
          inputClassName={`bg-white shadow-none outline-1 outline-gray-300 md:rounded-lg h-10 ${
            errors?.school?.message && "ring-error ring-1"
          }`}
          className="w-full text-sm"
          type="text"
          required
          {...register("school", {
            required: t("team.school_required"),
          })}
        />

        <div className="flex gap-2 mt-4">
          <Button
            text={t("buttons.cancel")}
            color="secondary"
            type="button"
            onClick={onCancel}
          />
          <Button
            text={t("buttons.save")}
            color="primary"
            type="submit"
            loading={loading || loadingOptions}
            disabled={loading || loadingOptions}
          />
        </div>
      </form>
    </div>
  );
}
